import React, { useEffect, useRef, useState } from "react";

import { Download, Paperclip } from "lucide-react";

import { latestText } from "./MailThread";

import "./MailThread.css";

const plain = (value) => {
  if (value == null) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(plain).filter(Boolean).join(", ");
  }

  if (typeof value === "object") {
    return plain(
      value.name || value.address || value.text || value.value || "",
    );
  }

  return "";
};

const fileKind = (name = "", type = "") => {
  const ext = String(name).split(".").pop().toLowerCase();

  if (type === "application/pdf" || ext === "pdf")
    return { cls: "pdf", label: "PDF" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { cls: "xls", label: "XLS" };
  if (["doc", "docx"].includes(ext)) return { cls: "doc", label: "DOC" };
  if (["ppt", "pptx"].includes(ext)) return { cls: "ppt", label: "PPT" };
  if (String(type).startsWith("image/")) return { cls: "img", label: "IMG" };
  if (["zip", "rar", "7z"].includes(ext)) return { cls: "zip", label: "ZIP" };

  return { cls: "", label: ext.slice(0, 4).toUpperCase() || "FILE" };
};

const formatSize = (bytes) => {
  const n = Number(bytes);

  if (!Number.isFinite(n)) return "Size unavailable";

  return n >= 1024 * 1024
    ? `${(n / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;
};

// one line shown under the sender name while the card is closed
const snippetOf = (item, body) => {
  if (body?.text !== undefined) {
    return (
      body.text.replace(/\s+/g, " ").slice(0, 120) || "(No text in this mail)"
    );
  }

  if (body?.error) return "(Unable to load this mail)";

  if (item.preview) return item.preview;

  return body?.loading ? "Loading..." : "Click to read";
};

// other mails of the same conversation (Inbox + Sent), one card each
export default function MailHistory({
  items,
  fetchDetail,
  resetKey,
  onOpenAttachment,
  attachmentBusy = "",
}) {
  const [open, setOpen] = useState({});
  const [bodies, setBodies] = useState({});

  // mails whose text was already asked for (so it is fetched only once)
  const requested = useRef(new Set());
  const hoverTimer = useRef(null);

  // another mail opened -> start fresh
  useEffect(() => {
    setOpen({});
    setBodies({});
    requested.current = new Set();
    clearTimeout(hoverTimer.current);
  }, [resetKey]);

  useEffect(() => () => clearTimeout(hoverTimer.current), []);

  // fetch the text (and attachments) of one mail, only once
  const loadBody = (item) => {
    if (requested.current.has(item.key)) return;

    requested.current.add(item.key);

    setBodies((previous) => ({ ...previous, [item.key]: { loading: true } }));

    fetchDetail(item)
      .then((message) => {
        setBodies((previous) => ({
          ...previous,
          [item.key]: {
            text: latestText(message.body),
            to: plain(message.to),
            attachments: Array.isArray(message.attachments)
              ? message.attachments
              : [],
          },
        }));
      })
      .catch((err) => {
        // allow a new try on the next hover / click
        requested.current.delete(item.key);

        setBodies((previous) => ({
          ...previous,
          [item.key]: { error: err.message || "Unable to load this mail." },
        }));
      });
  };

  const toggle = (item) => {
    setOpen((previous) => ({ ...previous, [item.key]: !previous[item.key] }));

    loadBody(item);
  };

  // mouse rests on a card for a moment -> load its text for the snippet
  const hoverStart = (item) => {
    clearTimeout(hoverTimer.current);

    hoverTimer.current = setTimeout(() => loadBody(item), 300);
  };

  const hoverEnd = () => clearTimeout(hoverTimer.current);

  return (
    <div className="erp-thread">
      <div className="erp-thread-label">
        Conversation &middot; {items.length} other{" "}
        {items.length === 1 ? "message" : "messages"}
      </div>

      {items.map((item) => {
        const isOpen = Boolean(open[item.key]);
        const body = bodies[item.key];
        const isSent = item.folder === "sent";
        const attachments = body?.attachments || [];

        const name = isSent
          ? "Me"
          : String(item.sender || "")
              .replace(/\s*<[^>]*>/, "")
              .trim() || item.sender;

        return (
          <div
            key={item.key}
            className={`erp-thread-msg ${isOpen ? "open" : ""}`}
          >
            <button
              type="button"
              className="erp-thread-head"
              aria-expanded={isOpen}
              onClick={() => toggle(item)}
              onMouseEnter={() => hoverStart(item)}
              onMouseLeave={hoverEnd}
            >
              <span
                className="erp-thread-avatar"
                style={{ backgroundColor: item.color }}
              >
                {isSent ? "ME" : item.initials}
              </span>

              <span className="erp-thread-main">
                <strong>{name}</strong>

                {isOpen ? (
                  <small>
                    {isSent ? "To: " : "From: "}
                    {isSent
                      ? body?.to || item.email
                      : String(item.sender || "")}
                  </small>
                ) : (
                  <span className="erp-thread-snippet">
                    {snippetOf(item, body)}
                  </span>
                )}
              </span>

              {(item.hasAttachments || attachments.length > 0) && (
                <Paperclip size={14} className="erp-thread-clip" />
              )}

              <small className="erp-thread-date">{item.date}</small>
            </button>

            {isOpen && (
              <div className="erp-thread-text">
                {body?.loading && (
                  <span className="erp-thread-note">Loading...</span>
                )}

                {body?.error && (
                  <span className="erp-thread-note error">{body.error}</span>
                )}

                {body?.text !== undefined &&
                  (body.text || "(No text in this mail)")}
              </div>
            )}

            {/* attachments of this mail: click card = open, icon = download */}
            {isOpen && attachments.length > 0 && (
              <div className="erp-email-attachments">
                <h4>{attachments.length} Attachments</h4>

                <div className="erp-email-attachment-grid">
                  {attachments.map((attachment, index) => {
                    const kind = fileKind(
                      attachment.filename,
                      attachment.contentType,
                    );

                    const isBusy = attachmentBusy.startsWith(
                      `${item.key}-${attachment.index}-`,
                    );

                    const openIt = () =>
                      onOpenAttachment?.(attachment, false, item);

                    return (
                      <div
                        key={`${attachment.index ?? index}-${attachment.filename || ""}`}
                        className={`erp-email-attachment ${
                          isBusy ? "busy" : ""
                        }`}
                        role="button"
                        tabIndex={0}
                        title="Click to open"
                        onClick={openIt}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openIt();
                          }
                        }}
                      >
                        <button
                          type="button"
                          className="erp-email-attachment-download"
                          title="Download"
                          onClick={(event) => {
                            event.stopPropagation();
                            onOpenAttachment?.(attachment, true, item);
                          }}
                        >
                          <Download size={16} />
                        </button>

                        <span className={`erp-file-badge ${kind.cls}`}>
                          {kind.label}
                        </span>

                        <strong>
                          {attachment.filename || `Attachment ${index + 1}`}
                        </strong>

                        <small>
                          {isBusy ? "Opening..." : formatSize(attachment.size)}
                        </small>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
